import { join, dirname } from 'path';
import { homedir, platform } from 'os';
import fs from 'fs-extra';
import { logger } from './logger.js';
import {
  CLAUDE_MD_MARKER_BEGIN,
  CLAUDE_MD_MARKER_END,
  SUPPORT_MD_MARKER_BEGIN,
  SUPPORT_MD_MARKER_END,
  buildClaudeMdBlock,
  buildSupportMdBlock,
} from './claude-rules.js';

// ============================================================================
// JSONC Support
// ============================================================================

export function stripJsonComments(text: string): string {
  let result = '';
  let inString = false;
  let stringChar = '';
  let i = 0;

  while (i < text.length) {
    if (inString) {
      if (text[i] === '\\') {
        result += text[i] + (text[i + 1] || '');
        i += 2;
        continue;
      }
      if (text[i] === stringChar) {
        inString = false;
      }
      result += text[i];
      i++;
      continue;
    }

    if (text[i] === '"' || text[i] === "'") {
      inString = true;
      stringChar = text[i];
      result += text[i];
      i++;
      continue;
    }

    if (text[i] === '/' && text[i + 1] === '/') {
      while (i < text.length && text[i] !== '\n') i++;
      continue;
    }

    if (text[i] === '/' && text[i + 1] === '*') {
      i += 2;
      while (i < text.length && !(text[i] === '*' && text[i + 1] === '/')) i++;
      i += 2;
      continue;
    }

    result += text[i];
    i++;
  }

  result = result.replace(/,\s*([\]}])/g, '$1');

  return result;
}

// ============================================================================
// Claude Code Settings (~/.claude/settings.json)
// ============================================================================

export function getClaudeSettingsPath(): string {
  return join(homedir(), '.claude', 'settings.json');
}

/**
 * Read + parse a settings.json (JSONC-tolerant) at an arbitrary path.
 * Returns null when the file is absent OR cannot be parsed — callers that must
 * not clobber an unparseable file should check `fs.pathExists` separately.
 */
async function readSettingsAt(settingsPath: string): Promise<Record<string, unknown> | null> {
  try {
    if (!(await fs.pathExists(settingsPath))) {
      return null;
    }
    const raw = await fs.readFile(settingsPath, 'utf-8');
    return JSON.parse(stripJsonComments(raw));
  } catch {
    return null;
  }
}

/**
 * Write a settings.json at an arbitrary path, backing up any existing file
 * first (`.backup-<timestamp>.json`).
 */
async function writeSettingsAt(
  settingsPath: string,
  settings: Record<string, unknown>,
): Promise<{ path: string; backupPath: string | null }> {
  let backupPath: string | null = null;

  await fs.ensureDir(dirname(settingsPath));

  if (await fs.pathExists(settingsPath)) {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    backupPath = settingsPath.replace(/\.json$/, `.backup-${timestamp}.json`);
    await fs.copy(settingsPath, backupPath);
  }

  await fs.writeFile(settingsPath, JSON.stringify(settings, null, 2), 'utf-8');

  return { path: settingsPath, backupPath };
}

export async function readClaudeSettings(): Promise<Record<string, unknown> | null> {
  return readSettingsAt(getClaudeSettingsPath());
}

async function writeClaudeSettings(
  settings: Record<string, unknown>,
): Promise<{ path: string; backupPath: string | null }> {
  return writeSettingsAt(getClaudeSettingsPath(), settings);
}

/**
 * Configure Claude Code environment variables in ~/.claude/settings.json.
 */
export async function configureClaudeEnvironment(): Promise<{ success: boolean; path: string; error?: string }> {
  const settingsPath = getClaudeSettingsPath();

  try {
    const existingSettings = await readClaudeSettings();
    if (existingSettings === null && await fs.pathExists(settingsPath)) {
      logger.warning('Cannot parse existing settings.json — skipping to avoid data loss');
      return { success: false, path: settingsPath, error: 'Cannot parse existing settings.json' };
    }
    const settings = existingSettings || {};
    const env = (settings['env'] || {}) as Record<string, string>;

    let changed = false;

    if (env['CLAUDE_CODE_MAX_OUTPUT_TOKENS'] !== '64000') {
      env['CLAUDE_CODE_MAX_OUTPUT_TOKENS'] = '64000';
      changed = true;
    }

    if (!changed) {
      return { success: true, path: settingsPath };
    }

    settings['env'] = env;
    await writeClaudeSettings(settings);

    return { success: true, path: settingsPath };
  } catch (error) {
    return {
      success: false,
      path: settingsPath,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// Claude Code Status Line (~/.claude/settings.json → statusLine)
// ============================================================================

/** Relative path of the deployed status line script under a `.claude` dir. */
const STATUSLINE_SCRIPT_REL = ['scripts', 'statusline', 'index.js'] as const;

/** True if a `statusLine.command` already points at our deployed script. */
function ownsStatusLineCommand(command: unknown): boolean {
  return typeof command === 'string' && /scripts[\\/]statusline[\\/]index\.js/.test(command);
}

export type StatusLineConfigStatus =
  | 'set' // wrote (added or replaced) our status line
  | 'unchanged' // already exactly ours
  | 'kept-foreign' // a custom status line exists — left untouched (no --force)
  | 'missing-script' // the deployed script is not on disk — refused to point at it
  | 'error';

/**
 * Wire the SmartStack status line into `<claudeDir>/settings.json`, pointing at
 * the script deployed under `<claudeDir>/scripts/statusline/index.js`.
 *
 * Non-destructive by default: if the user already has a *different* status line,
 * it is left untouched unless `force` is set. Any existing settings.json is
 * backed up before write (via writeSettingsAt). Never points settings at a
 * script that isn't on disk.
 */
export async function configureStatusLine(
  claudeDir: string,
  options: { force?: boolean } = {},
): Promise<{ status: StatusLineConfigStatus; path: string; error?: string }> {
  const settingsPath = join(claudeDir, 'settings.json');
  const scriptPath = join(claudeDir, ...STATUSLINE_SCRIPT_REL);

  try {
    // Never point settings at a script that wasn't deployed.
    if (!(await fs.pathExists(scriptPath))) {
      return { status: 'missing-script', path: settingsPath };
    }

    const existed = await fs.pathExists(settingsPath);
    const parsed = await readSettingsAt(settingsPath);
    if (parsed === null && existed) {
      // File exists but is unparseable — refuse to overwrite and lose data.
      return { status: 'error', path: settingsPath, error: 'Cannot parse existing settings.json' };
    }
    const settings = parsed || {};

    const desired = {
      type: 'command',
      // Forward slashes work for `node` on every platform and avoid JSON escaping.
      command: `node "${scriptPath.replace(/\\/g, '/')}"`,
      padding: 0,
      // Re-run on a 2s timer on top of the event triggers (new assistant message,
      // /compact, permission-mode, vim). Live session state that is NOT a trigger —
      // reasoning effort, rate-limit gauges, a branch changed by a background agent —
      // then refreshes on its own instead of staying stale until the next message.
      refreshInterval: 2,
    };

    const existing = settings['statusLine'] as Record<string, unknown> | undefined;

    // Respect a user's own status line unless we already own it or --force.
    if (existing && !ownsStatusLineCommand(existing['command']) && !options.force) {
      return { status: 'kept-foreign', path: settingsPath };
    }

    if (
      existing &&
      existing['type'] === desired.type &&
      existing['command'] === desired.command &&
      existing['padding'] === desired.padding &&
      existing['refreshInterval'] === desired.refreshInterval
    ) {
      return { status: 'unchanged', path: settingsPath };
    }

    settings['statusLine'] = desired;
    await writeSettingsAt(settingsPath, settings);
    return { status: 'set', path: settingsPath };
  } catch (error) {
    return {
      status: 'error',
      path: settingsPath,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// VS Code Settings — path resolution for MCP cleanup
// ============================================================================

interface VsCodeSettingsCandidate {
  path: string;
  variant: 'Code' | 'Code - Insiders';
}

function getVsCodeSettingsPaths(): VsCodeSettingsCandidate[] {
  const home = homedir();
  const os = platform();

  if (os === 'win32') {
    const appdata = process.env.APPDATA || join(home, 'AppData', 'Roaming');
    return [
      { path: join(appdata, 'Code', 'User', 'settings.json'), variant: 'Code' },
      { path: join(appdata, 'Code - Insiders', 'User', 'settings.json'), variant: 'Code - Insiders' },
    ];
  }
  if (os === 'darwin') {
    return [
      { path: join(home, 'Library', 'Application Support', 'Code', 'User', 'settings.json'), variant: 'Code' },
      { path: join(home, 'Library', 'Application Support', 'Code - Insiders', 'User', 'settings.json'), variant: 'Code - Insiders' },
    ];
  }
  return [
    { path: join(home, '.config', 'Code', 'User', 'settings.json'), variant: 'Code' },
    { path: join(home, '.config', 'Code - Insiders', 'User', 'settings.json'), variant: 'Code - Insiders' },
  ];
}

// ============================================================================
// v5 Migration — remove legacy MCP server registrations (smartstack, context7)
// ============================================================================

const LEGACY_MCP_SERVER_NAMES = ['smartstack', 'context7'];

export interface McpCleanupResult {
  cleaned: string[];
  errors: string[];
}

/**
 * Remove the legacy SmartStack/Context7 MCP server entries from:
 *  - ~/.claude/settings.json           → mcpServers.smartstack, mcpServers.context7
 *  - VS Code user settings.json        → claudeCode.mcpServers.smartstack, .context7
 *
 * Each touched file is backed up before write (`.backup-<timestamp>.json`).
 * Used by `ss install --force` (auto-triggered on version upgrade) and `ss upgrade`
 * to migrate users from v4.x to v5.0 without leaving orphan MCP registrations.
 */
export async function removeMcpServerRegistration(): Promise<McpCleanupResult> {
  const result: McpCleanupResult = { cleaned: [], errors: [] };

  // 1. Clean ~/.claude/settings.json
  try {
    const claudePath = getClaudeSettingsPath();
    if (await fs.pathExists(claudePath)) {
      const settings = await readClaudeSettings();
      if (settings === null) {
        result.errors.push(`${claudePath}: unparseable, skipped`);
      } else if (settings['mcpServers'] && typeof settings['mcpServers'] === 'object') {
        const mcpServers = settings['mcpServers'] as Record<string, unknown>;
        let touched = false;
        for (const name of LEGACY_MCP_SERVER_NAMES) {
          if (name in mcpServers) {
            delete mcpServers[name];
            touched = true;
          }
        }
        if (touched) {
          if (Object.keys(mcpServers).length === 0) {
            delete settings['mcpServers'];
          }
          await writeClaudeSettings(settings);
          result.cleaned.push(claudePath);
        }
      }
    }
  } catch (error) {
    result.errors.push(`Claude settings: ${error instanceof Error ? error.message : 'unknown'}`);
  }

  // 2. Clean VS Code user settings (stable + Insiders)
  for (const candidate of getVsCodeSettingsPaths()) {
    try {
      if (!(await fs.pathExists(candidate.path))) continue;

      const raw = await fs.readFile(candidate.path, 'utf-8');
      let settings: Record<string, unknown>;
      try {
        settings = JSON.parse(stripJsonComments(raw));
      } catch {
        result.errors.push(`${candidate.path}: unparseable, skipped`);
        continue;
      }

      const mcpServers = settings['claudeCode.mcpServers'];
      if (!mcpServers || typeof mcpServers !== 'object') continue;

      const servers = mcpServers as Record<string, unknown>;
      let touched = false;
      for (const name of LEGACY_MCP_SERVER_NAMES) {
        if (name in servers) {
          delete servers[name];
          touched = true;
        }
      }

      if (!touched) continue;

      if (Object.keys(servers).length === 0) {
        delete settings['claudeCode.mcpServers'];
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const backupPath = candidate.path.replace('.json', `.backup-${timestamp}.json`);
      await fs.copy(candidate.path, backupPath);
      await fs.writeFile(candidate.path, JSON.stringify(settings, null, 4), 'utf-8');
      result.cleaned.push(candidate.path);
    } catch (error) {
      result.errors.push(`${candidate.path}: ${error instanceof Error ? error.message : 'unknown'}`);
    }
  }

  return result;
}

// ============================================================================
// SmartStack Git Safety — PreToolUse git-guard hook (<claudeDir>/settings.json)
// ============================================================================

/** A hook command string is "ours" when it points at the deployed guard script matching `re`. */
function ownsGuardCommand(command: unknown, re: RegExp): boolean {
  return typeof command === 'string' && re.test(command);
}

export type GitGuardConfigStatus = 'set' | 'unchanged' | 'removed' | 'absent' | 'missing-script' | 'error';

/**
 * Register (or remove) a PreToolUse Bash guard hook in <claudeDir>/settings.json.
 * Shared by git-guard and ef-guard (see the thin wrappers below) — the only
 * per-guard difference is the deployed script filename + the "owns" regex.
 *
 * Claude Code reads hooks from settings.json (NOT from hooks/hooks.json — that is
 * a plugin-only manifest), so wiring it here is what makes the guard actually
 * fire. The merge is non-destructive: existing hooks/keys are preserved, the
 * file is backed up before write, and re-running is idempotent (the entry is
 * updated in place, never duplicated). Never points at a script not on disk.
 */
async function configureBashGuard(
  claudeDir: string,
  guard: { scriptFile: string; scriptRe: RegExp; matcher?: string },
  options: { force?: boolean; remove?: boolean } = {},
): Promise<{ status: GitGuardConfigStatus; path: string; error?: string }> {
  const settingsPath = join(claudeDir, 'settings.json');
  const scriptPath = join(claudeDir, 'hooks', guard.scriptFile);
  const desiredCommand = `node "${scriptPath.replace(/\\/g, '/')}"`;

  try {
    const existed = await fs.pathExists(settingsPath);
    const parsed = await readSettingsAt(settingsPath);
    if (parsed === null && existed) {
      return { status: 'error', path: settingsPath, error: 'Cannot parse existing settings.json' };
    }

    // ---- remove mode ----
    if (options.remove) {
      if (!parsed) return { status: 'absent', path: settingsPath };
      const hooks = parsed['hooks'] as Record<string, unknown> | undefined;
      const pre = hooks && (hooks['PreToolUse'] as unknown);
      if (!hooks || !Array.isArray(pre)) return { status: 'absent', path: settingsPath };

      let touched = false;
      const cleanedGroups = (pre as Array<Record<string, unknown>>)
        .map((group) => {
          const gh = group && (group['hooks'] as unknown);
          if (!Array.isArray(gh)) return group;
          const kept = (gh as Array<Record<string, unknown>>).filter((h) => {
            const isOurs = ownsGuardCommand(h && h['command'], guard.scriptRe);
            if (isOurs) touched = true;
            return !isOurs;
          });
          return { ...group, hooks: kept };
        })
        .filter((group) => Array.isArray(group['hooks']) && (group['hooks'] as unknown[]).length > 0);

      if (!touched) return { status: 'absent', path: settingsPath };

      if (cleanedGroups.length > 0) {
        hooks['PreToolUse'] = cleanedGroups;
      } else {
        delete hooks['PreToolUse'];
        if (Object.keys(hooks).length === 0) delete parsed['hooks'];
      }
      await writeSettingsAt(settingsPath, parsed);
      return { status: 'removed', path: settingsPath };
    }

    // ---- install mode ----
    if (!(await fs.pathExists(scriptPath))) {
      return { status: 'missing-script', path: settingsPath };
    }

    const settings = parsed || {};
    const hooks = (settings['hooks'] as Record<string, unknown>) || {};
    const pre = Array.isArray(hooks['PreToolUse'])
      ? (hooks['PreToolUse'] as Array<Record<string, unknown>>)
      : [];

    // Look for an existing git-guard hook anywhere in PreToolUse.
    let foundExact = false;
    let foundStale: Record<string, unknown> | null = null;
    for (const group of pre) {
      const gh = group && (group['hooks'] as unknown);
      if (!Array.isArray(gh)) continue;
      for (const h of gh as Array<Record<string, unknown>>) {
        if (ownsGuardCommand(h['command'], guard.scriptRe)) {
          if (h['command'] === desiredCommand) foundExact = true;
          else foundStale = h;
        }
      }
    }

    if (foundExact && !options.force) return { status: 'unchanged', path: settingsPath };

    if (foundStale) {
      // Update a stale path (e.g. an old .sh) in place — never duplicate.
      foundStale['type'] = 'command';
      foundStale['command'] = desiredCommand;
    } else if (!foundExact) {
      pre.push({ matcher: guard.matcher ?? 'Bash', hooks: [{ type: 'command', command: desiredCommand }] });
    }
    hooks['PreToolUse'] = pre;
    settings['hooks'] = hooks;
    await writeSettingsAt(settingsPath, settings);
    return { status: 'set', path: settingsPath };
  } catch (error) {
    return { status: 'error', path: settingsPath, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/** Register (or remove) the git-guard PreToolUse hook (blocks unapproved git writes). */
export function configureGitGuardHook(
  claudeDir: string,
  options: { force?: boolean; remove?: boolean } = {},
): Promise<{ status: GitGuardConfigStatus; path: string; error?: string }> {
  return configureBashGuard(claudeDir, { scriptFile: 'git-guard.cjs', scriptRe: /git-guard\.(c?js|sh)/i }, options);
}

/** Register (or remove) the ef-guard PreToolUse hook (blocks raw `dotnet ef` writes — see migration policy). */
export function configureEfGuardHook(
  claudeDir: string,
  options: { force?: boolean; remove?: boolean } = {},
): Promise<{ status: GitGuardConfigStatus; path: string; error?: string }> {
  return configureBashGuard(claudeDir, { scriptFile: 'ef-guard.cjs', scriptRe: /ef-guard\.(c?js|sh)/i }, options);
}

/** Register (or remove) the skills-guard PreToolUse hook on the file-editing
 *  tools — blocks Claude from editing the DEPLOYED copies under
 *  <claudeDir>/{skills,agents,hooks,commands} (client sites have no CLI
 *  sources; the sanctioned path for a genuine CLI defect is /support-report). */
export function configureSkillsGuardHook(
  claudeDir: string,
  options: { force?: boolean; remove?: boolean } = {},
): Promise<{ status: GitGuardConfigStatus; path: string; error?: string }> {
  return configureBashGuard(
    claudeDir,
    { scriptFile: 'skills-guard.cjs', scriptRe: /skills-guard\.(c?js|sh)/i, matcher: 'Edit|Write|MultiEdit|NotebookEdit' },
    options,
  );
}

/** Register (or remove) the ba-audit-guard PreToolUse hook on the Task tool —
 *  blocks a subagent prompted to apply the BA audit rules BY HAND over the
 *  `.smartstack/ba/` corpus (the 394M-token incident shape); the sanctioned
 *  path is the deterministic audit-ba CLI (/ba-audit-run), which the guard
 *  lets through by citation. */
export function configureBaAuditGuardHook(
  claudeDir: string,
  options: { force?: boolean; remove?: boolean } = {},
): Promise<{ status: GitGuardConfigStatus; path: string; error?: string }> {
  return configureBashGuard(
    claudeDir,
    { scriptFile: 'ba-audit-guard.cjs', scriptRe: /ba-audit-guard\.(c?js|sh)/i, matcher: 'Task' },
    options,
  );
}

// ============================================================================
// SmartStack Git Safety — managed CLAUDE.md block (<claudeDir>/CLAUDE.md)
// ============================================================================

export type ClaudeMdConfigStatus = 'created' | 'updated' | 'unchanged' | 'removed' | 'absent' | 'error';

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

async function backupFileWithTimestamp(filePath: string): Promise<string | null> {
  if (!(await fs.pathExists(filePath))) return null;
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const backupPath = `${filePath}.backup-${timestamp}`;
  await fs.copy(filePath, backupPath);
  return backupPath;
}

/**
 * Merge (or remove) ONE managed block in <claudeDir>/CLAUDE.md. Each block is
 * delimited by its own HTML-comment markers, so the operation is idempotent,
 * blocks coexist independently, and only the text BETWEEN a block's markers is
 * ever touched — anything the user wrote elsewhere (including the OTHER
 * managed blocks) is preserved. The file is backed up before any write.
 */
async function configureManagedMdBlock(
  claudeDir: string,
  managed: { begin: string; end: string; block: string },
  options: { force?: boolean; remove?: boolean } = {},
): Promise<{ status: ClaudeMdConfigStatus; path: string; backupPath?: string | null; error?: string }> {
  const mdPath = join(claudeDir, 'CLAUDE.md');
  const block = managed.block;
  const blockRe = new RegExp(
    `${escapeRegExp(managed.begin)}[\\s\\S]*?${escapeRegExp(managed.end)}`,
  );

  try {
    const exists = await fs.pathExists(mdPath);
    const current = exists ? await fs.readFile(mdPath, 'utf-8') : '';

    // ---- remove mode ----
    if (options.remove) {
      if (!exists || !blockRe.test(current)) return { status: 'absent', path: mdPath };
      let next = current.replace(blockRe, '').replace(/\n{3,}/g, '\n\n').replace(/^\s+/, '');
      const backupPath = await backupFileWithTimestamp(mdPath);
      if (next.trim().length === 0) {
        await fs.remove(mdPath);
      } else {
        if (!next.endsWith('\n')) next += '\n';
        await fs.writeFile(mdPath, next, 'utf-8');
      }
      return { status: 'removed', path: mdPath, backupPath };
    }

    // ---- install / merge mode ----
    if (!exists) {
      await fs.ensureDir(dirname(mdPath));
      await fs.writeFile(mdPath, block + '\n', 'utf-8');
      return { status: 'created', path: mdPath };
    }

    if (blockRe.test(current)) {
      const existingBlock = current.match(blockRe)?.[0] ?? '';
      if (existingBlock === block && !options.force) return { status: 'unchanged', path: mdPath };
      if (existingBlock === block) return { status: 'unchanged', path: mdPath };
      const backupPath = await backupFileWithTimestamp(mdPath);
      await fs.writeFile(mdPath, current.replace(blockRe, block), 'utf-8');
      return { status: 'updated', path: mdPath, backupPath };
    }

    // File exists but has no managed block — append it.
    const backupPath = await backupFileWithTimestamp(mdPath);
    const sep = current.endsWith('\n') ? '\n' : '\n\n';
    await fs.writeFile(mdPath, current + sep + block + '\n', 'utf-8');
    return { status: 'updated', path: mdPath, backupPath };
  } catch (error) {
    return { status: 'error', path: mdPath, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/** Merge (or remove) the managed "SmartStack Git Safety" block in <claudeDir>/CLAUDE.md. */
export function configureClaudeMd(
  claudeDir: string,
  options: { force?: boolean; remove?: boolean } = {},
): Promise<{ status: ClaudeMdConfigStatus; path: string; backupPath?: string | null; error?: string }> {
  return configureManagedMdBlock(
    claudeDir,
    { begin: CLAUDE_MD_MARKER_BEGIN, end: CLAUDE_MD_MARKER_END, block: buildClaudeMdBlock() },
    options,
  );
}

/** Merge (or remove) the managed "SmartStack CLI Support" block in
 *  <claudeDir>/CLAUDE.md — the SOFT layer telling Claude it is never the
 *  CLI's maintainer at a client site (/support-report is the sanctioned path;
 *  the skills-guard hook is the HARD layer). */
export function configureSupportRulesMd(
  claudeDir: string,
  options: { force?: boolean; remove?: boolean } = {},
): Promise<{ status: ClaudeMdConfigStatus; path: string; backupPath?: string | null; error?: string }> {
  return configureManagedMdBlock(
    claudeDir,
    { begin: SUPPORT_MD_MARKER_BEGIN, end: SUPPORT_MD_MARKER_END, block: buildSupportMdBlock() },
    options,
  );
}
